import { useCallback } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import {
  useGetCreatedFertsQuery,
  useGetFertCommentsQuery,
  useGetFertilizationDonorSchedulesQuery,
  useGetFertilizationPulledSemenQuery,
  useGetFertTechsQuery,
  useGetInlineFertCommentsQuery,
  useGetTravelingTechsQuery,
  useCreateFertilizationMutation as useGqlCreateFertilizationMutation,
  useUpsertReverseSortMutation as useGqlUpsertReverseSortMutation,
  useLazyGetFertilizationScheduledDonorsQuery,
} from '@/app/services/fertilizationApp';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import {
  CommentItem,
  CreatedFertItem,
  createdFertsOptions,
  CreateFertilizationRequest,
  CreateFertilizationResponse,
  fertCommentsInGridOptions,
  fertCommentsOptions,
  FertDonorScheduleItem,
  fertDonorSchedulesOptions,
  FertPulledSemenItem,
  fertPulledSemenOptions,
  FertScheduledDonorItem,
  fertScheduledDonorsOptions,
  fertTechsOptions,
  TechItem,
  UpsertReverseSortRequest,
  useCreateFertilizationRestMutation,
  useUpsertReverseSortRestMutation,
} from '@/services/fertilization';

const FLAG = '29386_fertilization_graphql_to_rest';

export function useCreatedFertsQuery(params: {
  donorScheduleDate: string | null;
  fertSiteId: string | null;
}) {
  const isRest = useFeatureFlag(FLAG);
  const enabled = !!params.donorScheduleDate && !!params.fertSiteId;

  const restQuery = useQuery({
    ...createdFertsOptions({
      donorScheduleDate: params.donorScheduleDate!,
      fertSiteId: params.fertSiteId!,
    }),
    enabled: isRest && enabled,
  });

  const gqlQuery = useGetCreatedFertsQuery(
    {
      donorScheduleDate: params.donorScheduleDate as string,
      fertSiteId: params.fertSiteId as string,
    },
    { skip: isRest || !enabled },
  );

  if (isRest) {
    const mappedData = Array.isArray(restQuery.data)
      ? restQuery.data.map((item: any) => ({
          ...item,
          id: item.guidValue,
        }))
      : [];
    return {
      data: mappedData,
      isLoading: restQuery.isFetching,
      error: restQuery.error,
      refetch: () => {
        void restQuery.refetch();
      },
    };
  }
  return {
    data: gqlQuery.data?.createdFerts?.items as CreatedFertItem[] | undefined,
    isLoading: gqlQuery.isLoading,
    error: null,
    refetch: () => {
      void gqlQuery.refetch();
    },
  };
}

export function useFertPulledSemenQuery(params: {
  donorScheduleDate: string | null;
  fertSiteId: string | null;
}) {
  const isRest = useFeatureFlag(FLAG);
  const enabled = !!params.donorScheduleDate && !!params.fertSiteId;

  const restQuery = useQuery({
    ...fertPulledSemenOptions({
      donorScheduleDate: params.donorScheduleDate!,
      fertSiteId: params.fertSiteId!,
    }),
    enabled: isRest && enabled,
  });

  const gqlQuery = useGetFertilizationPulledSemenQuery(
    {
      donorScheduleDate: params.donorScheduleDate as string,
      fertSiteId: params.fertSiteId as string,
    },
    { skip: isRest || !enabled },
  );

  if (isRest) {
    return {
      data: restQuery.data,
      isLoading: restQuery.isFetching,
      error: restQuery.error,
      refetch: () => {
        void restQuery.refetch();
      },
    };
  }
  return {
    data: gqlQuery.data?.pulledSemen?.items as
      | FertPulledSemenItem[]
      | undefined,
    isLoading: gqlQuery.isLoading,
    error: null,
    refetch: () => {
      void gqlQuery.refetch();
    },
  };
}

export function useFertDonorSchedulesQuery(params: {
  donorScheduleDate: string | null;
  fertSiteId: string | null;
}) {
  const isRest = useFeatureFlag(FLAG);
  const enabled = !!params.donorScheduleDate && !!params.fertSiteId;

  const restQuery = useQuery({
    ...fertDonorSchedulesOptions({
      donorScheduleDate: params.donorScheduleDate!,
      fertSiteId: params.fertSiteId!,
    }),
    enabled: isRest && enabled,
  });

  const gqlQuery = useGetFertilizationDonorSchedulesQuery(
    {
      donorScheduleDate: params.donorScheduleDate as string,
      fertSiteId: params.fertSiteId as string,
    },
    { skip: isRest || !enabled },
  );

  if (isRest) {
    return {
      data: restQuery.data,
      isLoading: restQuery.isFetching,
      error: restQuery.error,
      refetch: () => {
        void restQuery.refetch();
      },
    };
  }
  return {
    data: gqlQuery.data?.fertilizationDonorSchedules?.items as
      | FertDonorScheduleItem[]
      | undefined,
    isLoading: gqlQuery.isLoading,
    error: null,
    refetch: () => {
      void gqlQuery.refetch();
    },
  };
}

export function useFertScheduledDonorsLazy() {
  const isRest = useFeatureFlag(FLAG);
  const queryClient = useQueryClient();
  const [gqlScheduledDonors] = useLazyGetFertilizationScheduledDonorsQuery();

  const fetchScheduledDonors = useCallback(
    async (params: {
      semenID: string;
      donorScheduleDate: string;
      semenType: string;
      accountID: string;
    }): Promise<FertScheduledDonorItem[]> => {
      if (isRest) {
        return queryClient.fetchQuery(
          fertScheduledDonorsOptions({
            donorScheduleDate: params.donorScheduleDate,
            semenId: params.semenID,
            accountId: params.accountID,
            semenType: params.semenType,
          }),
        );
      }
      const result = await gqlScheduledDonors({
        semenID: params.semenID,
        donorScheduleDate: params.donorScheduleDate,
        semenType: params.semenType,
        accountID: params.accountID,
      }).unwrap();
      return (result?.scheduledDonors?.items ?? []) as FertScheduledDonorItem[];
    },
    [isRest, queryClient, gqlScheduledDonors],
  );

  return fetchScheduledDonors;
}

export function useFertTechsQuery(params: { fertSiteId: string | null }) {
  const isRest = useFeatureFlag(FLAG);
  const enabled = !!params.fertSiteId;

  const restQuery = useQuery({
    ...fertTechsOptions({ fertSiteId: params.fertSiteId }),
    enabled: isRest && enabled,
  });

  const gqlQuery = useGetFertTechsQuery(
    { fertSiteId: params.fertSiteId },
    { skip: isRest || !enabled },
  );

  if (isRest) {
    return {
      data: restQuery.data,
      isLoading: restQuery.isFetching,
      error: restQuery.error,
    };
  }
  return {
    data: gqlQuery.data?.techs?.items as TechItem[] | undefined,
    isLoading: gqlQuery.isLoading,
    error: null,
  };
}

export function useTravelingTechsQuery() {
  const isRest = useFeatureFlag(FLAG);

  const restQuery = useQuery({
    ...fertTechsOptions({}),
    enabled: isRest,
  });

  const gqlQuery = useGetTravelingTechsQuery({}, { skip: isRest });

  if (isRest) {
    return {
      data: restQuery.data,
      isLoading: restQuery.isFetching,
      error: restQuery.error,
    };
  }
  return {
    data: gqlQuery.data?.techs?.items as TechItem[] | undefined,
    isLoading: gqlQuery.isLoading,
    error: null,
  };
}

export function useFertCommentsQuery() {
  const isRest = useFeatureFlag(FLAG);

  const restQuery = useQuery({
    ...fertCommentsOptions(),
    enabled: isRest,
  });

  const gqlQuery = useGetFertCommentsQuery({}, { skip: isRest });

  if (isRest) {
    return {
      data: restQuery.data,
      isLoading: restQuery.isFetching,
      error: restQuery.error,
    };
  }
  return {
    data: gqlQuery.data?.fertCommentsList?.items as CommentItem[] | undefined,
    isLoading: gqlQuery.isLoading,
    error: null,
  };
}

export function useInlineFertCommentsQuery() {
  const isRest = useFeatureFlag(FLAG);

  const restQuery = useQuery({
    ...fertCommentsInGridOptions(),
    enabled: isRest,
  });

  const gqlQuery = useGetInlineFertCommentsQuery({}, { skip: isRest });

  if (isRest) {
    return {
      data: restQuery.data,
      isLoading: restQuery.isFetching,
      error: restQuery.error,
    };
  }
  return {
    data: gqlQuery.data?.fertCommentsInGridList?.items as
      | CommentItem[]
      | undefined,
    isLoading: gqlQuery.isLoading,
    error: null,
  };
}

export function useCreateFertilization() {
  const isRest = useFeatureFlag(FLAG);
  const restMutation = useCreateFertilizationRestMutation();
  const [gqlMutate, { isLoading: gqlLoading }] =
    useGqlCreateFertilizationMutation();

  const mutateAsync = useCallback(
    async (
      request: CreateFertilizationRequest,
    ): Promise<CreateFertilizationResponse | undefined> => {
      if (isRest) {
        return restMutation.mutateAsync(request);
      }
      const result = await gqlMutate(request).unwrap();
      return result?.createFertilizations as
        | CreateFertilizationResponse
        | undefined;
    },
    [isRest, restMutation, gqlMutate],
  );

  return {
    mutateAsync,
    isLoading: isRest ? restMutation.isPending : gqlLoading,
  };
}

export function useUpsertReverseSort() {
  const isRest = useFeatureFlag(FLAG);
  const restMutation = useUpsertReverseSortRestMutation();
  const [gqlMutate, { isLoading: gqlLoading }] =
    useGqlUpsertReverseSortMutation();

  const mutateAsync = useCallback(
    async (request: UpsertReverseSortRequest) => {
      if (isRest) {
        return restMutation.mutateAsync(request);
      }
      return gqlMutate(request).unwrap();
    },
    [isRest, restMutation, gqlMutate],
  );

  return {
    mutateAsync,
    isLoading: isRest ? restMutation.isPending : gqlLoading,
  };
}
